import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useParams } from 'react-router';
import { Box, HStack, VStack, Input, createListCollection } from '@chakra-ui/react';
import {
  fetchEntity,
  registerEntities,
  selectEntities,
  selectSaveSuccess,
} from '../../slices/masterData/entityManagerSlice';
import EntityEditor from './EntityEditor';
import EntityTable from './EntityTable';
import allSchema from './schemas/entitySchemas';
import basicSchema from './schemas/entityBasicSchemas';
import { isManualPriceStrategy } from '../../utils';
import { fetchConfigs } from '../../actions/actions_config';
import HorizontalNavigationBand from '../core/HorizontalNavigationBand';
import { FaSearch } from 'react-icons/fa';
import DashboardSectionTile from '../core/DashboardSectionTile';
import { PaginationBar } from '../core/PaginationComponents';
import AddEntityButton from './AddEntityButton';
import api from '../../api';
import Constants from '../../Constants';
import PropTypes from 'prop-types';
import { Alert } from '../ui/alert';
import { InputGroup } from '../ui/input-group';
import { SelectContent, SelectItem, SelectRoot, SelectTrigger, SelectValueText } from '../ui/select';

const paginationOptions = createListCollection({
  items: [
    { value: 10, label: 'Show 10' },
    { value: 20, label: 'Show 20' },
    { value: 30, label: 'Show 30' },
    { value: 40, label: 'Show 40' },
    { value: 50, label: 'Show 50' },
  ],
});

const renderTabComponent = ({ allEntitiesSchema, mainPath, entityType, TabsComponent }) => {
  return <TabsComponent {...{ allEntitiesSchema, mainPath, entityType }} />;
};

function findReferencedEntities(entityDefinition, allEntities, allEntitiesSchema) {
  let referencedEntities = {};
  ((entityDefinition && entityDefinition.fields) || [])
    .filter(
      fieldDefinition => fieldDefinition.dataType === 'reference' || fieldDefinition.dataType === 'simple-reference'
    )
    .forEach(refField => {
      const name = refField.referenceEntityType;
      const foundEntity = allEntitiesSchema.find(ent => ent.name === name);
      if (allEntities[name]) {
        // If it's already fetched.
        referencedEntities[name] = allEntities[name];
      } else {
        referencedEntities[name] = {
          entityDefinition: foundEntity,
          entities: [],
        };
      }
      // Some entity can nest itself which causes infinite loop.
      if (name !== entityDefinition.name) {
        // Recursively find referenced entity (for nested field)
        const recursiveNestedRef = findReferencedEntities(foundEntity, allEntities, allEntitiesSchema);
        referencedEntities = Object.assign(referencedEntities, recursiveNestedRef);
      }
    });
  return referencedEntities;
}

const MdmBaseComponent = props => {
  const [showEditModel, setShowEditModel] = useState(false);
  const [activeEntityid, setActiveEntityid] = useState(null);

  // Here we use item offsets; we could also use page offsets
  // following the API or data you're working with.
  const [paginationState, setPaginationState] = useState({
    itemsPerPage: 10,
    currentPage: 1,
    pageCount: 0,
  });

  const [searchState, setSearchState] = useState({
    query: '',
    entities: [],
  });
  const [mdmLogs, setMdmLogs] = useState({ items: [], pagination: { page: 1, pages: 0, page_size: 10, total: 0 } });
  const [mdmFilters, setMdmFilters] = useState({});

  const dispatch = useDispatch();
  // const saveSuccessMessage = useSelector(selectSaveSuccessMessage);
  const saveSuccess = useSelector(selectSaveSuccess);

  const user = useSelector(state => state.user);
  const configs = useSelector(state => state.configs);
  const { schema } = props;

  let allEntitiesSchema = [];
  if (schema) {
    allEntitiesSchema = schema;
  } else {
    allEntitiesSchema = isManualPriceStrategy(configs) ? basicSchema : allSchema;
  }

  const allEntities = useSelector(selectEntities);
  let { entityType } = useParams();
  if (!entityType && allEntitiesSchema) {
    // Default entity to edit.
    entityType = allEntitiesSchema[0].name;
  }

  // This component can be used for /user-management or /master-data
  const location = useLocation();
  const mainPath = location?.pathname.split('/')[1];

  const theseEntities = (allEntities && allEntities[entityType]) || {
    entities: [],
  };
  const entityDefinition = theseEntities.entityDefinition || null;
  const referencedEntities = findReferencedEntities(entityDefinition, allEntities, allEntitiesSchema);

  
  const fetchMdmLogs = async (page, page_size) => {
    try {
      const params = {
        page,
        page_size,
        ...Object.entries(mdmFilters || {}).reduce((acc, [k, v]) => {
          if (v !== undefined && v !== null && v !== '') acc[k] = v;
          return acc;
        }, {}),
      };
      const response = await api.get('/mdm-activity-logs', { params });
      const json = await Constants.handleErrors(response, dispatch);
      const pagination = json.pagination || {
        page: json.page,
        page_size: json.page_size || json.limit,
        total: json.total,
        pages: json.pages,
        has_next: json.has_next,
        has_previous: json.has_previous,
        next_page: json.next_page,
        previous_page: json.previous_page,
      };
      setMdmLogs({ items: json.items || [], pagination });
    } catch (e) {
      // swallow, error handling via Constants.handleErrors
    }
  };

  useEffect(() => {
    dispatch(fetchConfigs(user.token));
  }, []);

  useEffect(() => {
    // Load entities schema.
    dispatch(registerEntities({ entities: allEntitiesSchema }));
    if (entityDefinition && mdmLogs.items.length === 0) {
      fetchMdmLogs(paginationState.currentPage, paginationState.itemsPerPage);
    }
    // Fetch referenced entities.
    Object.entries(referencedEntities).forEach(([, entity]) => {
      // Fetch data.
      if (entity && entity.entities.length === 0) {
        if (!entity.entityDefinition.nested) {
          // Do not fetch nested entity as it's already in main entity data.
          dispatch(fetchEntity(entity.entityDefinition));
        }
      }
    });
  }, [entityDefinition, allEntitiesSchema]);

  useEffect(() => {
    if (theseEntities.entities || searchState.entities) {
      const { itemsPerPage } = paginationState;
      setPaginationState({
        ...paginationState,
        pageCount: Math.ceil(
          (searchState.query ? searchState.entities.length : mdmLogs.items.length) / itemsPerPage
        ),
      });
    }
  }, [theseEntities.entities, searchState.entities, searchState.query]);

  // Invoke when user click to request another page.
  const handlePageClick = selected => {
    setPaginationState(prev => ({ ...prev, currentPage: selected }));
    fetchMdmLogs(selected, paginationState.itemsPerPage);
  };

  const handleSearch = e => {
    const { value: searchInputValue } = e.target;
    const searchFields = entityDefinition.fields.filter(field => !field.hideList);
    const sourceEntities = mdmLogs.items;
    const searchResults = sourceEntities.filter(entity =>
      searchFields.some(searchField => {
        const { dataType, referenceType, name: searchFieldName } = searchField;
        const data = entity[searchFieldName];
        let entityValue;
        if (typeof data === 'string' || typeof data === 'number') {
          entityValue = data;
        }
        if (['boolean'].includes(dataType)) {
          entityValue = data ? 'Yes' : 'No';
        }
        if (dataType === 'reference' && referenceType === 'nested') {
          entityValue = data
            ? Object.values(data).reduce((prev, current) => {
                if (current && current.length) {
                  prev += ` ${current}`;
                }
                return prev;
              }, '')
            : '';
        }
        if (dataType === 'reference' && ['multi-select', 'select'].includes(referenceType)) {
          const refEnt = referencedEntities[searchField.referenceEntityType];
          if (refEnt) {
            let refEntId = refEnt.entityDefinition.systemIdField;
            let refEntDisplayId = refEnt.entityDefinition.displayIdField;
            let { entities } = refEnt;
            if (searchField.referenceEntitySubType) {
              // For form fileds reference, we have to look into sub entity type.
              entities = entities.filter(ent => ent.type === searchField.referenceEntitySubType);
              refEntId = 'value';
              refEntDisplayId = 'value';
            }
            const match = entities.filter(ent => {
              const value = refEntId ? ent[refEntId] : ent;
              if (Array.isArray(data)) {
                return data.includes(value);
              }
              return value === data;
            });

            let processedValue;

            if (match && match.length) {
              if (match.length > 1) {
                processedValue = match.map(value => (value[refEntDisplayId] ? value[refEntDisplayId] : value)).join('');
              } else {
                processedValue = match[0][refEntDisplayId];
              }
            } else {
              processedValue = 'None';
            }
            entityValue = processedValue;
          }
        }

        return entityValue ? `${entityValue}`.toLowerCase().includes(searchInputValue.toLowerCase()) : false;
      })
    );
    setSearchState({
      ...searchState,
      query: searchInputValue,
      entities: searchResults,
    });
  };

  const handleItemsPerPageSelect = e => {
    const currentPageValue = 1;
    const itemsPerPageValue = parseInt(e.value[0], 10);

    setPaginationState({
      ...paginationState,
      itemsPerPage: itemsPerPageValue,
      currentPage: currentPageValue,

      pageCount: Math.ceil(
        (searchState.query ? searchState.entities.length : mdmLogs.items.length) / itemsPerPageValue
      ),
    });
    fetchMdmLogs(currentPageValue, itemsPerPageValue);
  };

  useEffect(() => {
    if (saveSuccess === true && searchState.query) {
      handleSearch({
        target: {
          value: searchState.query,
        },
      });
    }
  }, [saveSuccess, searchState.query]);

  if (!entityDefinition) return false;

  return (
    <>
      <HorizontalNavigationBand justifyContent="flex-start" paddingX="47px">
        {renderTabComponent({ allEntitiesSchema, mainPath, entityType, TabsComponent: props.TabsComponent })}
      
      </HorizontalNavigationBand>
      <VStack background="white" align="stretch" gap="28px" marginY="43px" paddingX="61px">
        <DashboardSectionTile
          title={
            entityDefinition.shouldNotPluralize ? entityDefinition.displayName : `${entityDefinition.displayName}s`
          }
        >
          {(mdmLogs.items && mdmLogs.items.length) ? (
            <>
              <InputGroup marginTop="5px !important" endElement={<FaSearch color="#0000005E" />}>
                <Input
                  marginLeft="auto !important"
                  placeholder="Search"
                  width="264px"
                  height="40px"
                  value={searchState.query}
                  backgroundColor="#ffffff"
                  onChange={handleSearch}
                />
              </InputGroup>
              <EntityTable
                entityDefinition={entityDefinition}
                entities={searchState.query ? searchState.entities : mdmLogs.items}
                referencedEntities={referencedEntities}
                setActiveEntityid={setActiveEntityid}
                setShowEditModel={setShowEditModel}
                pageSize={paginationState.itemsPerPage}
                currentPage={mdmLogs.pagination?.page || 1}
              />
            </>
          ) : (
            <Alert
              status="warning"
              bg="warningBg"
              borderColor="warningBorder"
              w="500px"
              style={{ textAlign: 'center', margin: ' 20vh auto' }}
            >
              <Box style={{ backgroundColor: 'transparent', fontSize: '15px', color: 'lightgrey' }}>No Data</Box>
            </Alert>
          )}
        </DashboardSectionTile>
        <HStack marginLeft="auto" marginTop="20px" justifyContent="space-between" width="100%">
          <SelectRoot collection={paginationOptions} width="140px" onValueChange={e => handleItemsPerPageSelect(e)}>
            <SelectTrigger>
              <SelectValueText placeholder="Show 20" />
            </SelectTrigger>
            <SelectContent>
              {paginationOptions.items.map(opt => (
                <SelectItem item={opt} key={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </SelectRoot>
          <PaginationBar
            currentPage={mdmLogs.pagination?.page || 1}
            pages={mdmLogs.pagination?.pages || 0}
            onPageChange={page => handlePageClick(page)}
            justifyContent="flex-end"
          />
        </HStack>
      </VStack>
      <EntityEditor
        entityDefinition={entityDefinition}
        referencedEntities={referencedEntities}
        showEditModel={showEditModel}
        setShowEditModel={setShowEditModel}
        entityType={entityType}
        activeEntityid={activeEntityid}
        setActiveEntityid={setActiveEntityid}
      />
    </>
  );
};

MdmBaseComponent.propTypes = {
  schema: PropTypes.instanceOf(Object),
  TabsComponent: PropTypes.node,
};

export default MdmBaseComponent;
